<template>
  <div>
    <div class="options">
      <div
        ref="optionsList"
        :class="['list', 'gantt_scroll']"
        :style="{maxHeight: `${maxHeight}px`}"
      >
        <Container
          drag-handle-selector=".option-drag-handle"
          :lock-axis="'y'"
          :auto-scroll-enabled="true"
          @drop="onDrop(null, optionsItems, $event)"
        >
          <Draggable
            v-for="(item, index) in optionsItems"
            :key="index"
            ref="tagOptionItem"
            :class="['list__item', item.id === targetOptionId ? 'active' : '', error && index === 0 ? 'is_error' : '']"
          >
            <div
              v-if="optionsItems.length > 1"
              class="dnd_icon option-drag-handle"
            >
              <svg-sprite
                :name="'dragdrop'"
                :size="20"
              />
            </div>
            <div
              class="color_bg"
              :style="{background: item.color}"
              @click="togglePalette(item.id, index)"
            />
            <div
              :class="['option_input', item.readonly && optionsItems.length > 1 ? 'option-drag-handle' : '']"
              @click="clickEdit(item.id)"
            >
              <vgp-input
                ref="tagOptionInput"
                :data-id="item.id"
                :placeholder="locale('custom_column_item_placeholder')"
                :model-value="item.value"
                :border="false"
                :small="true"
                :change-by-enter="true"
                :use-tab-key="true"
                @onChange="changeValue($event, item.id)"
                @update:modelValue="changeValue($event, item.id)"
                @onBlur="changeValue($event, item.id)"
                @onKeyup="addOptionByEnter"
                @onFocus="$emit('resetError')"
                @onKeydown="onKeydown($event, item)"
              />
            </div>
            <div
              v-if="optionsItems.length > 1"
              class="columns_bar button_delete"
            >
              <icon-button
                :icon="{ name:'delete', size: 20 }"
                size="small"
                @onClick="deleteOption(item.id)"
              />
            </div>
          </Draggable>
        </Container>
      </div>
      <div class="button_add">
        <vgp-button
          class="gp_autotest_columns_bar_add_tag_option"
          :label="locale('common_add')"
          type="text"
          :icon="{name: 'plus-1', type: 'bold', size: 20}"
          icon-side="left"
          :x-small="true"
          @onClick="addOption"
        />
      </div>
    </div>

    <context-menu
      v-if="isShowColorsDropdown"
      :position="positionColorsDropdown"
      @onClickOutside="onClickOutside"
    >
      <template #body>
        <vgp-colors-list
          :colors="colors"
          :selected="[selectedColorId]"
          @selectColors="onChangeColor"
        />
      </template>
    </context-menu>
  </div>
</template>

<script>
import { Container, Draggable } from 'vue3-smooth-dnd';
import iconButton from '../../../common/VueComponents/globalButton/withIcon/iconButton.vue';
import vgpButton from '../../../common/VueComponents/globalButton/vgpButton.vue';
import vgpColorsList from '../../../common/VueComponents/colorPalette/colorsList/colorsList.vue';
import columnsBarHelpers from '../../helpers';
import svgSprite from '../../../common/VueComponents/VueIcon/svgSprite.vue';

export default {
  name: 'ColumnsBarTagOptions',
  components: {
    vgpColorsList, vgpButton, iconButton, Container, Draggable, svgSprite,
  },
  props: {
    options: { type: Array, required: false, default: () => [] },
    error: { type: Boolean, requied: true, default: false },
    maxHeight: { type: Number, required: false, default: 500 },
  },
  emits: {
    onChange: Array,
    resetError: null,
  },
  data() {
    return {
      locale: __,
      optionsItems: [],
      usedColors: [],
      targetOptionId: null,
      selectedColorId: null,
      positionColorsDropdown: null,
      isShowColorsDropdown: false,
    };
  },
  computed: {
    colors() {
      return this.$store.getters['teamSetting/colors'].data;
    },
  },
  watch: {
    options: {
      handler(options) {
        if (options && options.length) {
          this.setOptions(options);
        }
      },
      immediate: true,
    },
  },
  created() {
    if (this.options && this.options.length) {
      this.setOptions(this.options);
    } else {
      this.setDefault();
    }
    this.$emit('onChange', this.optionsItems);
  },
  beforeUnmount() {
    this.optionsItems = [];
    this.usedColors = [];
  },
  methods: {
    setOptions(options) {
      let newOptionsItems = [];
      let newUsedColors = [];

      if (this.optionsItems.length) {
        const existingOptions = this.optionsItems.filter(opt => options.find(item => item.id === opt.id || opt.newLocalOption));

        existingOptions.forEach(opt => {
          const usedColor = this.colors.find(i => i.hex === opt.color);
          const externalOption = options.find(item => item.id === opt.id);

          if (opt.changedLocalOption || opt.newLocalOption) {
            newOptionsItems.push(opt);
          } else {
            newOptionsItems.push({
              id: externalOption.id,
              colorId: usedColor.id,
              color: externalOption.color,
              value: externalOption.value,
              is_default: externalOption.is_default,
              sort_order: externalOption.sort_order,
              readonly: true,
            });
          }

          newUsedColors.push(usedColor);
        });
      } else {
        options.forEach((opt, index) => {
          const usedColor = this.colors.find(i => i.hex === opt.color);

          newOptionsItems.push({
            id: opt.id,
            colorId: usedColor.id,
            color: opt.color,
            value: opt.value,
            is_default: opt.is_default,
            sort_order: opt.sort_order,
            readonly: true,
          });

          newUsedColors.push(usedColor);
        });
      }

      newOptionsItems.sort((a, b) => a.sort_order - b.sort_order);

      this.optionsItems = newOptionsItems;
      this.usedColors = newUsedColors;
    },
    setDefault() {
      const defaultColor = this.colors.find(color => color.id === 2);
      const newId = new Date().getTime();

      this.optionsItems.push({
        id: newId,
        colorId: defaultColor.id,
        color: defaultColor.hex,
        value: '',
        is_default: 0,
        sort_order: this.optionsItems.length,
        readonly: true,
      });
      this.usedColors.push(defaultColor);

      setTimeout(() => {
        this.focusElement(newId);
      }, 0);
    },
    togglePalette(id, index) {
      this.isShowColorsDropdown = !this.isShowColorsDropdown;

      const colorId = this.optionsItems.find(opt => opt.id === id).colorId;
      const optionElement = this.$refs.tagOptionItem[index].$el;

      if (this.isShowColorsDropdown) {
        this.targetOptionId = id;
        this.positionColorsDropdown = optionElement.getBoundingClientRect();
        this.selectedColorId = +colorId;
      } else {
        this.targetOptionId = null;
      }
    },
    deleteOption(id) {
      this.optionsItems = this.optionsItems.filter(opt => opt.id !== id);
      this.$emit('onChange', this.optionsItems);
    },
    addOption() {
      const randomColor = columnsBarHelpers.getRandomColor(this.colors, this.usedColors);
      const newId = new Date().getTime();

      this.optionsItems.push({
        id: newId,
        colorId: randomColor.id,
        color: randomColor.hex,
        value: '',
        is_default: 0,
        sort_order: this.optionsItems.length,
        readonly: true,
        newLocalOption: true,
      });
      this.usedColors.push(randomColor);
      this.$emit('onChange', this.optionsItems);

      setTimeout(() => {
        this.scrollList();
        this.focusElement(newId);
      }, 0);
    },
    addOptionByEnter(e) {
      if (e.key === 'Enter') {
        this.addOption();
      }
    },
    onChangeColor(data) {
      const newColor = data[0];
      const currentTagOption = this.optionsItems.find(opt => opt.id === this.targetOptionId);

      if (!currentTagOption) return;

      const index = this.optionsItems.indexOf(currentTagOption);

      this.optionsItems.splice(index, 1, {
        ...currentTagOption,
        colorId: newColor.id,
        color: newColor.hex,
        changedLocalOption: true,
      });
      this.$emit('onChange', this.optionsItems);
      this.onClickOutside();
    },
    onKeydown(e, item) {
      if (e.key === 'Tab') {
        const minObject = this.optionsItems.reduce((min, obj) => (obj.sort_order < min.sort_order ? obj : min), this.optionsItems[0]);
        const maxObject = this.optionsItems.reduce((max, obj) => (obj.sort_order > max.sort_order ? obj : max), this.optionsItems[0]);
        const filteredObjects = this.optionsItems.filter(obj => obj.sort_order > item.sort_order);
        const result = filteredObjects.reduce((min, obj) => (obj.sort_order < min.sort_order ? obj : min), filteredObjects[0]);

        this.changeValue(e, item.id);
        if (+item.sort_order === +maxObject.sort_order) {
          this.focusElement(minObject.id);
          this.clickEdit(minObject.id);
        } else {
          this.focusElement(result.id);
          this.clickEdit(result.id);
        }
        e.preventDefault();
      }
    },
    focusElement(id) {
      const inputElement = this.$refs.tagOptionInput.find(ref => +ref.$el.dataset.id === +id);

      if (inputElement) {
        inputElement.focusElement();
      }
    },
    onClickOutside() {
      this.isShowColorsDropdown = false;
      this.targetOptionId = null;
    },
    changeValue(input, id) {
      const newValue = input.target ? input.target.value.trim() : input.trim();
      const currentOption = this.optionsItems.find(opt => opt.id === id);

      if (!currentOption) return;

      const index = this.optionsItems.indexOf(currentOption);

      this.optionsItems.splice(index, 1, {
        ...currentOption,
        value: newValue,
        readonly: true,
        changedLocalOption: true,
      });

      this.$emit('onChange', this.optionsItems);
    },
    scrollList() {
      const list = this.$refs.optionsList;

      list.scrollTop = list.scrollHeight;
    },
    onDrop(id, optionsItems, dropResult) {
      if (dropResult.removedIndex === null && dropResult.addedIndex === null) return optionsItems;
      if (dropResult.removedIndex === dropResult.addedIndex) return optionsItems;

      const result = [...optionsItems];
      let itemToAdd = null;

      if (dropResult.removedIndex !== null) {
        itemToAdd = result.splice(dropResult.removedIndex, 1)[0];
      }

      if (dropResult.addedIndex !== null) {
        result.splice(dropResult.addedIndex, 0, itemToAdd);
      }

      this.optionsItems = [];

      result.forEach((option, idx) => {
        this.optionsItems.push({ ...option, sort_order: idx });
      });

      this.$emit('onChange', this.optionsItems);
    },
    clickEdit(id) {
      this.optionsItems.forEach(opt => {
        if (opt.id === id) {
          opt.readonly = !opt.readonly;
        }
      });
    },
  },
};
</script>

<style src='./style.less' lang='less'></style>
